import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ApiService from '../services/api';
import { Child } from '../types';
import ChildActivityScreen from './ChildActivityScreen';
import './ChildrenScreen.css';
import { formatDateUK } from '../utils/dateFormat';

interface ActivityInvitation {
  id: number;
  invitation_uuid?: string;
  activity_id: number;
  activity_uuid?: string;
  activity_name: string;
  activity_description?: string;
  start_date: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
  location?: string;
  status: 'pending' | 'accepted' | 'declined';
  host_child_name?: string;
  host_family_name?: string;
  host_parent_name?: string;
  host_parent_email?: string;
  invited_child_uuid?: string;
  invited_child_name?: string;
  message?: string;
  created_at: string;
  website_url?: string;
  activity_type?: string;
  is_tentative?: boolean;
  source?: string;
}

interface ConnectionRequest {
  id: number;
  request_uuid: string;
  requester_name: string;
  requester_email: string;
  requester_family_name?: string;
  child_name?: string;
  target_child_name?: string;
  message?: string;
  created_at: string;
}

interface ChildrenScreenProps {
  onNavigateToCalendar?: () => void;
  onNavigateToChildCalendar?: (child: Child, activityDate?: string) => void;
  onNavigateToChildActivities?: (child: Child) => void;
  onNavigateToActivity?: (child: Child, activity: any) => void;
  onNavigateBack?: () => void;
  initialSelectedChildId?: string | null;
  initialActivityUuid?: string;
  onChildSelectionChange?: (childId: string | null) => void;
  onNavigateToConnections?: (isInActivityCreation?: boolean) => void;
  shouldRestoreActivityCreation?: boolean;
  refreshTrigger?: number; // Add trigger to force refresh when prop changes
}

interface SystemNotification {
  id: string;
  type: 'system' | 'activity_reminder' | 'connection_rejected' | 'connection_accepted' | 'activity_rejected' | 'activity_accepted' | 'guest_decline' | 'guest_decline_pending' | 'tentative_response';
  title: string;
  message: string;
  timestamp: string;
  data?: any;
  child_uuid?: string;
}

const ChildrenScreen: React.FC<ChildrenScreenProps> = ({ onNavigateToCalendar, onNavigateToChildCalendar, onNavigateToChildActivities, onNavigateToActivity, onNavigateBack, initialSelectedChildId, initialActivityUuid, onChildSelectionChange, onNavigateToConnections, shouldRestoreActivityCreation, refreshTrigger }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [children, setChildren] = useState<Child[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newChildFirstName, setNewChildFirstName] = useState('');
  const [newChildLastName, setNewChildLastName] = useState('');
  const [newChildBirthDate, setNewChildBirthDate] = useState('');
  const [newChildSchoolVenue, setNewChildSchoolVenue] = useState('');
  const [newChildSchoolCity, setNewChildSchoolCity] = useState('');
  const [addingChild, setAddingChild] = useState(false);
  const [selectedChild, setSelectedChild] = useState<Child | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);


  const [editingChild, setEditingChild] = useState<Child | null>(null);
  const [editChildFirstName, setEditChildFirstName] = useState('');
  const [editChildLastName, setEditChildLastName] = useState('');
  const [editChildBirthDate, setEditChildBirthDate] = useState('');
  const [editChildInterests, setEditChildInterests] = useState('');
  const [editChildSchoolVenue, setEditChildSchoolVenue] = useState('');
  const [editChildSchoolCity, setEditChildSchoolCity] = useState('');
  const [updatingChild, setUpdatingChild] = useState(false);
  const [childActivityCounts, setChildActivityCounts] = useState<Record<string, number>>({});
  const [childInvitations, setChildInvitations] = useState<Record<string, ActivityInvitation[]>>({});
  const [childConnections, setChildConnections] = useState<Record<string, ConnectionRequest[]>>({});
  const [childSystemNotifications, setChildSystemNotifications] = useState<Record<string, SystemNotification[]>>({});
  // Removed: hostedActivityNotifications - now handled through system notifications
  const [processingInvitation, setProcessingInvitation] = useState<number | null>(null);
  const [processingConnection, setProcessingConnection] = useState<string | null>(null);
  const [activityDraft, setActivityDraft] = useState<any>(null);

  // Designated parent modal state
  const [showDesignatedParentModal, setShowDesignatedParentModal] = useState(false);
  const [selectedParentUuid, setSelectedParentUuid] = useState<string>('');
  const [parents, setParents] = useState<any[]>([]);
  const [pendingInvitationUuid, setPendingInvitationUuid] = useState<string>('');
  const [pendingInvitationAction, setPendingInvitationAction] = useState<'accept' | 'reject'>('accept');

  const apiService = ApiService.getInstance();

  // Helper function to detect if we're viewing individual child activities
  const isViewingChildActivities = () => {
    return location.pathname.includes('/children/') &&
           (location.pathname.includes('/activities') || selectedChild !== null);
  };

  // Check for editChild URL parameter and auto-open modal (simple approach like other modals)
  const searchParams = new URLSearchParams(window.location.search);
  const editChildUuid = searchParams.get('editChild');


  useEffect(() => {
    // Clean up any old non-user-specific drafts (security fix)
    localStorage.removeItem('activityDraft');

    loadChildren();
    loadParents();
    checkForActivityDraft();
  }, []);

  // Refresh children data when refreshTrigger prop changes
  useEffect(() => {
    if (refreshTrigger !== undefined) {
      // ChildrenScreen refresh triggered by prop change
      loadChildren();
    }
  }, [refreshTrigger]);

  // Listen for notification dismissals from NotificationBell
  useEffect(() => {
    const handleNotificationDismissed = (event: CustomEvent) => {
      // Reload notifications to update child badges
      // Skip when viewing individual child activities
      if (children.length > 0 && !isViewingChildActivities()) {
        loadInvitationsFromBell(children);
        loadConnectionRequestsForChildren(children);
        loadSystemNotificationsForChildren(children);
      }
    };

    window.addEventListener('notificationDismissed', handleNotificationDismissed as EventListener);

    return () => {
      window.removeEventListener('notificationDismissed', handleNotificationDismissed as EventListener);
    };
  }, [children]);

  // Check for activity draft when component becomes visible (user returns from connections tab)
  useEffect(() => {
    const handleFocus = () => {
      checkForActivityDraft();
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
  }, []);

  const checkForActivityDraft = () => {
    try {
      // Get user-specific draft key
      const currentUser = JSON.parse(localStorage.getItem('userData') || '{}');
      const draftKey = `activityDraft_${currentUser.id || 'unknown'}`;
      
      const draftStr = localStorage.getItem(draftKey);
      if (draftStr) {
        const draft = JSON.parse(draftStr);
        setActivityDraft(draft);
      }
    } catch (error) {
      console.error('Failed to load activity draft:', error);
    }
  };

  const resumeActivityCreation = () => {
    if (activityDraft && activityDraft.childUuid) {
      const child = children.find(c => c.uuid === activityDraft.childUuid);
      if (child) {
        // Clear the draft from ChildrenScreen since it will be handled by ChildActivityScreen
        setActivityDraft(null);
        setSelectedChild(child);
      }
    }
  };

  const dismissActivityDraft = () => {
    // Get user-specific draft key
    const currentUser = JSON.parse(localStorage.getItem('userData') || '{}');
    const draftKey = `activityDraft_${currentUser.id || 'unknown'}`;
    
    localStorage.removeItem(draftKey);
    setActivityDraft(null);
  };

  const loadParents = async () => {
    try {
      const response = await apiService.getParents();
      if (response.success && response.data) {
        setParents(response.data);
      }
    } catch (error) {
      console.error('Failed to load parents:', error);
      setParents([]);
    }
  };

  const loadChildren = async () => {
    try {
      // loadChildren called
      setLoading(true);
      const response = await apiService.getChildren();
      
      // loadChildren response
      
      if (response.success && response.data) {
        // Handle different response formats - API might return { data: [...] }
        const childrenData = Array.isArray(response.data) 
          ? response.data 
          : (response.data as any)?.data || [];
        
        // Setting children data
        // Children count: childrenData.length
        setChildren(childrenData);
        
        // Load activity counts and invitations together (consolidated to avoid duplicate API calls)
        // Skip when viewing individual child activities
        if (!isViewingChildActivities()) {
          await loadActivityCountsAndInvitations(childrenData);
        }

        // Skip connection requests when viewing individual child activities
        if (!isViewingChildActivities()) {
          await loadConnectionRequestsForChildren(childrenData);
        }

        // Skip notifications when viewing individual child activities
        if (!isViewingChildActivities()) {
          await loadSystemNotificationsForChildren(childrenData);
        }
        // Hosted activity notifications are now handled through system notifications
      } else {
        console.error('Failed to load children:', response.error);
        alert(`Error: ${response.error || 'Failed to load children'}`);
        setChildren([]); // Ensure children is always an array
      }
    } catch (error) {
      console.error('Load children error:', error);
      alert('Failed to load children');
      setChildren([]); // Ensure children is always an array
    } finally {
      setLoading(false);
    }
  };

  const loadActivityCountsAndInvitations = async (childrenData: Child[]) => {
    // Skip calendar activities API when viewing individual child activities
    if (isViewingChildActivities()) {
      return;
    }

    try {
      const counts: Record<string, number> = {};

      // Use same date range as ChildActivityScreen (current date to 1 year ahead)
      const now = new Date();
      const oneYearLater = new Date();
      oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
      const startDate = now.toISOString().split('T')[0];
      const endDate = oneYearLater.toISOString().split('T')[0];
      
      // ChildrenScreen - Using date range: startDate to endDate
      
      // Load activity count for each child using the SAME logic as ChildActivityScreen
      // ChildrenScreen - Loading owned activities
      const calendarActivitiesResponse = await apiService.getCalendarActivities(startDate, endDate);
      // ChildrenScreen - Calendar activities API response
      
      if (calendarActivitiesResponse.success && calendarActivitiesResponse.data) {
        const ownedActivities = Array.isArray(calendarActivitiesResponse.data) ? calendarActivitiesResponse.data : [];
        // ChildrenScreen - Owned activities from API: ${ownedActivities.length}
        
        // Process notifications from centralized system
        const invitedActivities: any[] = [];
        const pendingInvitations: any[] = [];
        
        // Combine all activities (owned + invited + pending)
        const allActivities = [...ownedActivities, ...invitedActivities, ...pendingInvitations];
        
        for (const child of childrenData) {
          try {
            // Filter activities the child is going to: hosted by them (private OR shared) OR accepted invitations
            const childActivities = allActivities.filter(activity => {
              const isHosting = activity.child_uuid === child.uuid; // Child is hosting (private or shared)
              const isAcceptedInvitation = activity.invited_child_uuid === child.uuid && 
                                          activity.invitation_status === 'accepted'; // Child accepted someone else's invite
              const shouldInclude = isHosting || isAcceptedInvitation;
              
              // Enhanced debug logging
              if (shouldInclude) {
              }
              
              return shouldInclude;
            });
            
            // Now filter to only count activities in the CURRENT WEEK (same as ChildActivityScreen week view)
            const currentWeek = new Date();
            const dayOfWeek = currentWeek.getDay(); // 0 = Sunday, 1 = Monday, etc.
            const startOfWeek = new Date(currentWeek);
            startOfWeek.setDate(currentWeek.getDate() - dayOfWeek); // Go back to Sunday
            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(startOfWeek.getDate() + 6); // Add 6 days to get to Saturday
            
            
            let weekActivitiesRaw = childActivities.filter(activity => {
              const activityStartDate = activity.start_date.split('T')[0];
              const activityEndDate = activity.end_date ? activity.end_date.split('T')[0] : activityStartDate;
              const startDate = startOfWeek.toISOString().split('T')[0];
              const endDate = endOfWeek.toISOString().split('T')[0];
              
              const isInCurrentWeek = (activityStartDate >= startDate && activityStartDate <= endDate) ||
                                    (activityEndDate >= startDate && activityEndDate <= endDate) ||
                                    (activityStartDate <= startDate && activityEndDate >= endDate);
              
              if (isInCurrentWeek) {
              }
              
              return isInCurrentWeek;
            });

            // EXPERIMENTAL: Group recurring activities to match ChildActivityScreen display logic
            // This might explain why 9 individual activities show as 2 grouped activities
            const recurringGroups = new Map();
            const singleActivities = [];
            
            weekActivitiesRaw.forEach(activity => {
              if (activity.series_id && activity.series_id !== 'undefined' && activity.series_id !== null) {
                // This is part of a recurring series
                if (!recurringGroups.has(activity.series_id)) {
                  recurringGroups.set(activity.series_id, []);
                }
                recurringGroups.get(activity.series_id).push(activity);
              } else {
                // Single activity
                singleActivities.push(activity);
              }
            });
            
            // Count: 1 for each recurring series + individual single activities
            const recurringSeriesCount = recurringGroups.size;
            const singleActivitiesCount = singleActivities.length;
            const groupedCount = recurringSeriesCount + singleActivitiesCount;
            
            
            // Use grouped count to match ChildActivityScreen behavior
            const weekActivities = { length: groupedCount }; // Fake array-like object for count
            
            // Count hosted vs accepted activities for detailed breakdown (use raw activities)
            const hostedCount = weekActivitiesRaw.filter(a => a.child_uuid === child.uuid).length;
            const acceptedCount = weekActivitiesRaw.filter(a => a.invited_child_uuid === child.uuid && a.invitation_status === 'accepted').length;
            
            counts[child.uuid] = weekActivities.length;
          } catch (error) {
            console.error(`Failed to load activity count for child ${child.uuid}:`, error);
            counts[child.uuid] = 0;
          }
        }
      } else {
        console.error('Failed to load calendar activities for counts:', calendarActivitiesResponse.error);
        // Set all counts to 0 if API fails
        for (const child of childrenData) {
          counts[child.uuid] = 0;
        }
      }
      
      setChildActivityCounts(counts);

      // Load invitations from centralized notification system and group by child
      // Skip when viewing individual child activities
      if (!isViewingChildActivities()) {
        await loadInvitationsFromBell(childrenData);
      }

    } catch (error) {
      console.error('Failed to load activity counts and invitations:', error);
    }
  };

  const loadInvitationsFromBell = async (childrenData: Child[]) => {
    // Skip when viewing individual child activities
    if (isViewingChildActivities()) {
      return;
    }

    try {
      // Use batch invitations API to get ACTUAL invitation data (not reconstructed from notifications)
      // This includes all fields: status, activity_source, etc.
      const now = new Date();
      const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      const oneYearAhead = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());

      const startDate = oneYearAgo.toISOString().split('T')[0];
      const endDate = oneYearAhead.toISOString().split('T')[0];

      const response = await apiService.getAllInvitationsForCalendar(startDate, endDate);

      if (response.success && response.data) {
        const invitationsByChild: Record<string, ActivityInvitation[]> = {};

        // Initialize empty arrays for each child
        childrenData.forEach(child => {
          invitationsByChild[child.uuid] = [];
        });

        // Group invitations by child - use ACTUAL invitation data from backend
        response.data.forEach((invitation: any) => {
          const targetChildUuid = invitation.invited_child_uuid;

          if (targetChildUuid && invitationsByChild[targetChildUuid]) {
            // Map backend invitation to ActivityInvitation interface
            const formattedInvitation: ActivityInvitation = {
              id: invitation.id,
              invitation_uuid: invitation.invitation_uuid || invitation.uuid,
              activity_id: invitation.activity_id || 0,
              activity_uuid: invitation.activity_uuid,
              activity_name: invitation.activity_name || 'Unknown Activity',
              activity_description: invitation.activity_description || '',
              start_date: invitation.start_date,
              end_date: invitation.end_date,
              start_time: invitation.start_time,
              end_time: invitation.end_time,
              location: invitation.location,
              status: invitation.status, // Use actual status from database
              host_child_name: invitation.host_child_name || '',
              host_family_name: '',
              host_parent_name: invitation.host_parent_name || '',
              host_parent_email: '',
              invited_child_uuid: targetChildUuid,
              invited_child_name: invitation.invited_child_name || '',
              message: invitation.message,
              invitation_message: invitation.invitation_message,
              created_at: invitation.created_at,
              website_url: invitation.website_url,
              activity_type: invitation.activity_type,
              is_tentative: invitation.is_tentative || false,
              activity_source: invitation.activity_source
            } as any;

            invitationsByChild[targetChildUuid].push(formattedInvitation);
          }
        });

        setChildInvitations(invitationsByChild);

        const totalInvitations = Object.values(invitationsByChild).reduce((sum, invitations) => sum + invitations.length, 0);
        console.log(`📊 Loaded ${totalInvitations} invitations from batch API`);
      } else {
        console.error('Failed to load batch invitations:', response.error);
        // Initialize empty arrays on error
        const invitationsByChild: Record<string, ActivityInvitation[]> = {};
        childrenData.forEach(child => {
          invitationsByChild[child.uuid] = [];
        });
        setChildInvitations(invitationsByChild);
      }
    } catch (error) {
      console.error('Failed to load batch invitations:', error);
      // Initialize empty arrays on error
      const invitationsByChild: Record<string, ActivityInvitation[]> = {};
      childrenData.forEach(child => {
        invitationsByChild[child.uuid] = [];
      });
      setChildInvitations(invitationsByChild);
    }
  };

  const loadConnectionRequestsForChildren = async (childrenData: Child[]) => {
    // Skip connection requests API when viewing individual child activities
    if (isViewingChildActivities()) {
      return;
    }

    try {
      const connectionsByChild: Record<string, ConnectionRequest[]> = {};

      // Initialize empty arrays for each child
      childrenData.forEach(child => {
        connectionsByChild[child.uuid] = [];
      });

      // Fetch ALL pending connection requests for the current user
      try {
        const connectionsResponse = await apiService.getConnectionRequests();

        if (connectionsResponse.success && connectionsResponse.data) {

          // Group ALL pending connection requests by target child UUID
          connectionsResponse.data.forEach((request: any) => {
            // Only include pending requests
            if (request.status === 'pending') {
              const targetChildUuid = request.target_child_uuid;
              if (targetChildUuid && connectionsByChild[targetChildUuid]) {
                connectionsByChild[targetChildUuid].push({
                  id: request.id,
                  request_uuid: request.request_uuid,
                  requester_name: request.requester_name,
                  requester_email: request.requester_email,
                  requester_family_name: request.requester_family_name,
                  child_name: request.child_name,
                  target_child_name: request.target_child_name,
                  message: request.message,
                  created_at: request.created_at
                });
              } else {
              }
            }
          });
        }
      } catch (error) {
        console.error('Failed to fetch connection request data:', error);
      }

      setChildConnections(connectionsByChild);

      const totalRequests = Object.values(connectionsByChild).reduce((sum, requests) => sum + requests.length, 0);
    } catch (error) {
      console.error('Failed to load connection requests:', error);
      // Initialize empty arrays on error
      const connectionsByChild: Record<string, ConnectionRequest[]> = {};
      childrenData.forEach(child => {
        connectionsByChild[child.uuid] = [];
      });
      setChildConnections(connectionsByChild);
    }
  };

  const loadSystemNotificationsForChildren = async (childrenData: Child[]) => {
    // Skip bell API when viewing individual child activities
    if (isViewingChildActivities()) {
      return;
    }

    try {
      const response = await apiService.getBellNotifications();
      
      if (response.success && response.data) {
        const systemNotificationsByChild: Record<string, SystemNotification[]> = {};
        
        // Initialize empty arrays for each child
        childrenData.forEach(child => {
          systemNotificationsByChild[child.uuid] = [];
        });
        
        // Process notifications and extract system messages, activity reminders, and response notifications
        response.data.forEach((notification: any) => {
          if (notification.type === 'system' ||
              notification.type === 'activity_reminder' ||
              notification.type === 'connection_rejected' ||
              notification.type === 'connection_accepted' ||
              notification.type === 'activity_rejected' ||
              notification.type === 'activity_accepted' ||
              notification.type === 'guest_decline' ||
              notification.type === 'guest_decline_pending' ||
              notification.type === 'tentative_response') {
            const data = notification.data || {};

            // Create system notification
            const systemNotification: SystemNotification = {
              id: notification.id,
              type: notification.type as 'system' | 'activity_reminder' | 'connection_rejected' | 'connection_accepted' | 'activity_rejected' | 'activity_accepted' | 'guest_decline' | 'guest_decline_pending' | 'tentative_response',
              title: notification.title,
              message: notification.message,
              timestamp: notification.timestamp,
              data: data,
              child_uuid: data.childUuid || data.child_uuid
            };
            
            // If it's related to a specific child, add it to that child's list
            if (systemNotification.child_uuid && systemNotificationsByChild[systemNotification.child_uuid]) {
              systemNotificationsByChild[systemNotification.child_uuid].push(systemNotification);
            } else {
              // If no specific child, add to all children (general system message)
              childrenData.forEach(child => {
                systemNotificationsByChild[child.uuid].push(systemNotification);
              });
            }
          }
        });
        
        setChildSystemNotifications(systemNotificationsByChild);
        
        const totalNotifications = Object.values(systemNotificationsByChild).reduce((sum, notifications) => sum + notifications.length, 0);
      } else {
        console.error('Failed to load system notifications:', response.error);
        // Initialize empty arrays on error
        const systemNotificationsByChild: Record<string, SystemNotification[]> = {};
        childrenData.forEach(child => {
          systemNotificationsByChild[child.uuid] = [];
        });
        setChildSystemNotifications(systemNotificationsByChild);
      }
    } catch (error) {
      console.error('Failed to load system notifications:', error);
      // Initialize empty arrays on error
      const systemNotificationsByChild: Record<string, SystemNotification[]> = {};
      childrenData.forEach(child => {
        systemNotificationsByChild[child.uuid] = [];
      });
      setChildSystemNotifications(systemNotificationsByChild);
    }
  };

  // Removed: loadHostedActivityNotifications function
  // Activity response notifications are now handled through system notifications

  const handleInvitationResponse = async (invitationUuid: string, action: 'accept' | 'reject') => {
    const formatDate = (dateString: string) => {
      return new Date(dateString).toLocaleDateString('en-GB', { 
        weekday: 'short', 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    };
    
    try {
      setProcessingInvitation(parseInt(invitationUuid)); // Keep for UI state management
      
      // ✅ RECURRING ACTIVITIES: Find the invitation and check if it's part of a series
      let currentInvitation: any = null;
      let allInvitationsForChild: any[] = [];
      
      Object.keys(childInvitations).forEach(childId => {
        const invitations = childInvitations[childId] || [];
        const found = invitations.find(inv => inv.invitation_uuid === invitationUuid || String(inv.id) === invitationUuid);
        if (found) {
          currentInvitation = found;
          allInvitationsForChild = invitations;
        }
      });
      
      if (!currentInvitation) {
        console.error('❌ Could not find invitation with UUID:', invitationUuid);
        return;
      }
      
      // Check if this is a recurring series invitation from notification
      if ((currentInvitation as any).isRecurringSeries) {
        
        const actionWord = action === 'accept' ? 'accept' : 'decline';
        const confirmMessage = `This is a recurring activity series with ${(currentInvitation as any).sessionCount} sessions (${(currentInvitation as any).dateRange}). Do you want to ${actionWord} the entire series?`;
        
        const confirmed = window.confirm(confirmMessage);
        if (!confirmed) {
          setProcessingInvitation(null);
          return;
        }
        
        // For series notifications, respond to the single notification (backend handles series logic)
        try {
          // Use the invitation UUID from the notification - backend will handle the series response
          const response = await apiService.respondToActivityInvitation(invitationUuid, action);
          
          if (response.success) {
            // Remove the series notification from the child's list
            setChildInvitations(prev => {
              const updated = { ...prev };
              Object.keys(updated).forEach(childId => {
                updated[childId] = updated[childId].filter(inv => inv.invitation_uuid !== invitationUuid);
              });
              return updated;
            });
            
            const sessionCount = (currentInvitation as any).sessionCount || 1;
            const message = action === 'accept' 
              ? `Series accepted! ${sessionCount} recurring activities will appear in your calendar.`
              : `Series declined. ${sessionCount} invitations removed.`;
            alert(message);
            
            loadChildren(); // Refresh to update all UI
          } else {
            alert(`Error: Failed to ${action} series invitation - ${response.error}`);
          }
        } catch (error) {
          console.error('Error processing series invitation:', error);
          alert(`Error: Failed to ${action} series invitation`);
        }
        
        setProcessingInvitation(null);
        return;
      }
      
      // No automatic grouping logic - recurring series are handled via backend notifications above

      // Check if this is a SOCS or Pitchero invitation and action is 'accept'
      if (action === 'accept' && (currentInvitation.source === 'socs' || currentInvitation.source === 'pitchero')) {
        // Show modal to select designated parent
        setPendingInvitationUuid(invitationUuid);
        setPendingInvitationAction(action);
        setShowDesignatedParentModal(true);
        setProcessingInvitation(null);
        return;
      }

      // Handle single invitation (either non-recurring or user chose single)
      const response = await apiService.respondToActivityInvitation(invitationUuid, action);
      
      if (response.success) {
        // Remove the invitation from the child's list
        setChildInvitations(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(childId => {
            updated[childId] = updated[childId].filter(inv => inv.invitation_uuid !== invitationUuid);
          });
          return updated;
        });
        
        // If invitation was accepted, increment club usage
        if (action === 'accept' && currentInvitation) {
          try {
            
            if (currentInvitation.website_url && currentInvitation.activity_type && currentInvitation.activity_uuid) {
              const clubUsageData = {
                website_url: currentInvitation.website_url,
                activity_type: currentInvitation.activity_type,
                location: currentInvitation.location || null,
                activity_uuid: currentInvitation.activity_uuid
              };
              
              
              const clubResponse = await apiService.incrementClubUsage(clubUsageData);
              if (clubResponse.success) {
              } else {
              }
            } else {
            }
          } catch (clubError) {
            console.error('❌ INVITATION ACCEPT: Failed to increment club usage:', clubError);
            // Don't fail the invitation acceptance if club increment fails
          }
        }
        
        const message = action === 'accept' 
          ? 'Invitation accepted! Activity will appear in your calendar.' 
          : 'Invitation declined.';
        alert(message);
        
        // Refresh data to update notification bell and other UI components
        loadChildren();
      } else {
        alert(`Error: ${response.error || 'Failed to respond to invitation'}`);
      }
    } catch (error) {
      console.error('Failed to respond to invitation:', error);
      alert('Failed to respond to invitation');
    } finally {
      setProcessingInvitation(null);
    }
  };

  const handleDesignatedParentConfirm = async (skip: boolean = false) => {
    if (!skip && !selectedParentUuid) {
      alert('Please select a designated parent');
      return;
    }

    setShowDesignatedParentModal(false);

    try {
      setProcessingInvitation(parseInt(pendingInvitationUuid));

      // Find the invitation again
      let currentInvitation: any = null;

      Object.keys(childInvitations).forEach(childId => {
        const invitations = childInvitations[childId] || [];
        const found = invitations.find(inv => inv.invitation_uuid === pendingInvitationUuid || String(inv.id) === pendingInvitationUuid);
        if (found) {
          currentInvitation = found;
        }
      });

      if (!currentInvitation) {
        console.error('❌ Could not find invitation with UUID:', pendingInvitationUuid);
        setProcessingInvitation(null);
        return;
      }

      // Call API with designated parent UUID (or undefined if skipped)
      const response = await apiService.respondToActivityInvitation(
        pendingInvitationUuid,
        pendingInvitationAction,
        skip ? undefined : selectedParentUuid
      );

      if (response.success) {
        // Remove the invitation from the child's list
        setChildInvitations(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(childId => {
            updated[childId] = updated[childId].filter(inv => inv.invitation_uuid !== pendingInvitationUuid);
          });
          return updated;
        });

        alert('Invitation accepted! Activity will appear in your calendar.');

        // Reload data to refresh activity status
        await loadChildren();
      } else {
        alert(`Error: ${response.error || 'Failed to respond to invitation'}`);
      }
    } catch (error) {
      console.error('Failed to respond to invitation:', error);
      alert('Failed to respond to invitation');
    } finally {
      setProcessingInvitation(null);
    }
  };

  const handleConnectionResponse = async (connectionUuid: string, action: 'accept' | 'reject') => {
    try {
      setProcessingConnection(connectionUuid);
      const response = await apiService.respondToConnectionRequest(connectionUuid, action);
      
      if (response.success) {
        // Remove the connection request from the child's list
        setChildConnections(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(childId => {
            updated[childId] = updated[childId].filter(req => req.request_uuid !== connectionUuid);
          });
          return updated;
        });
        
        const message = action === 'accept' 
          ? 'Connection request accepted!' 
          : 'Connection request declined.';
        alert(message);
        
        // Refresh data to update notification bell and other UI components
        loadChildren();
      } else {
        alert(`Error: ${response.error || 'Failed to respond to connection request'}`);
      }
    } catch (error) {
      console.error('Failed to respond to connection request:', error);
      alert('Failed to respond to connection request');
    } finally {
      setProcessingConnection(null);
    }
  };


  const handleAddChild = () => {
    setShowAddModal(true);
  };

  const handleCreateChild = async () => {
    if (!newChildFirstName.trim()) {
      alert('Please enter the child\'s first name');
      return;
    }
    
    if (!newChildLastName.trim()) {
      alert('Please enter the child\'s last name');
      return;
    }

    setAddingChild(true);
    try {
      // Calculate age from birth date if provided
      let age = undefined;
      if (newChildBirthDate) {
        const birthDate = new Date(newChildBirthDate);
        const today = new Date();
        age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
      }

      const childData = {
        first_name: newChildFirstName.trim(),
        last_name: newChildLastName.trim(),
        age: age,
        school_venue: newChildSchoolVenue.trim() || undefined,
        school_city: newChildSchoolCity.trim() || undefined
      };


      const response = await apiService.createChild(childData);


      if (response.success) {

        // Clear the form and close modal
        setNewChildFirstName('');
        setNewChildLastName('');
        setNewChildBirthDate('');
        setNewChildSchoolVenue('');
        setNewChildSchoolCity('');
        setShowAddModal(false);
        
        // Refresh data from API to get the latest state
        await loadChildren();

        // Notify parent component (Dashboard) to refresh its children state too
        if (typeof window !== 'undefined') {
          // Dispatch a custom event to notify Dashboard
          window.dispatchEvent(new CustomEvent('childrenDataChanged'));
          // Also dispatch childCreated to trigger welcome modal
          window.dispatchEvent(new CustomEvent('childCreated'));
        }
        
      } else {
        alert(`Error: ${response.error || 'Failed to add child'}`);
      }
    } catch (error) {
      alert('Failed to add child');
      console.error('Create child error:', error);
    } finally {
      setAddingChild(false);
    }
  };

  const handleDeleteChild = async (child: Child) => {
    if (window.confirm(`Are you sure you want to delete ${child.display_name || child.name}? This will also delete all their activities.`)) {
      try {
        const response = await apiService.deleteChild(child.uuid);
        if (response.success) {
          loadChildren();
          alert('Child deleted successfully');
        } else {
          alert(`Error: ${response.error || 'Failed to delete child'}`);
        }
      } catch (error) {
        alert('Failed to delete child');
        console.error('Delete child error:', error);
      }
    }
  };

  const handleEditChild = (child: Child) => {

    setEditingChild(child);

    // For skeleton children, parse the display_name or name field
    const displayName = child.display_name || child.name || '';
    const nameParts = displayName.split(' ');

    const firstName = child.first_name || nameParts[0] || '';
    const lastName = child.last_name || nameParts.slice(1).join(' ') || '';
    const interests = child.interests || '';
    const schoolVenue = child.school_venue || '';
    const schoolCity = child.school_city || '';

    // Calculate birth date from age if available
    let birthDate = '';
    if (child.age) {
      const currentYear = new Date().getFullYear();
      const birthYear = currentYear - child.age;
      birthDate = `${birthYear}-01-01`; // Default to January 1st
    }


    // Use setTimeout to ensure state updates are applied before modal opens
    setEditChildFirstName(firstName);
    setEditChildLastName(lastName);
    setEditChildBirthDate(birthDate);
    setEditChildInterests(interests);
    setEditChildSchoolVenue(schoolVenue);
    setEditChildSchoolCity(schoolCity);

    // Delay modal opening to ensure all state updates are applied
    setTimeout(() => {
      setShowEditModal(true);
    }, 10);
  };

  const handleUpdateChild = async () => {
    if (!editingChild) return;
    
    if (!editChildFirstName.trim()) {
      alert('Please enter the child\'s first name');
      return;
    }

    setUpdatingChild(true);
    try {
      // Calculate age from birth date
      let age = undefined;
      if (editChildBirthDate) {
        const birthDate = new Date(editChildBirthDate);
        const today = new Date();
        age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
      }

      const updateData = {
        first_name: editChildFirstName.trim(),
        last_name: editChildLastName.trim(),
        age: age,
        interests: editChildInterests.trim() || undefined,
        school_venue: editChildSchoolVenue.trim() || undefined,
        school_city: editChildSchoolCity.trim() || undefined
      };


      const response = await apiService.updateChild(editingChild.uuid, updateData);
      
      if (response.success) {

        // Check if this was a skeleton child being completed from onboarding flow
        const wasSkeletonChildEdit = editChildUuid && window.location.search.includes('editChild');

        // Clear URL parameter first to prevent auto-edit from triggering again
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('editChild');
        window.history.replaceState({}, '', newUrl.toString());

        // Close modal and refresh data
        setShowEditModal(false);
        setEditingChild(null);
        await loadChildren();

        // Notify parent component
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('childrenDataChanged'));

          // If this was a skeleton child being completed, emit special event to return to welcome modal
          if (wasSkeletonChildEdit) {
            window.dispatchEvent(new CustomEvent('skeletonChildCompleted'));
          }
        }

        alert('Child profile completed successfully');
      } else {
        alert(`Error: ${response.error || 'Failed to update child'}`);
      }
    } catch (error) {
      alert('Failed to update child');
      console.error('Update child error:', error);
    } finally {
      setUpdatingChild(false);
    }
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingChild(null);
    setEditChildFirstName('');
    setEditChildLastName('');
    setEditChildBirthDate('');
    setEditChildInterests('');
    setEditChildSchoolVenue('');
    setEditChildSchoolCity('');

    // Clear editChild URL parameter if present
    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.has('editChild')) {
      currentUrl.searchParams.delete('editChild');
      window.history.replaceState({}, '', currentUrl.toString());
    }
  };

  // Removed popstate handler - let React Router handle all navigation naturally

  // Handle initial selected child ID from parent
  useEffect(() => {
    if (initialSelectedChildId && children.length > 0) {
      const child = children.find(c => c.uuid === initialSelectedChildId);
      if (child) {
        setSelectedChild(child);
        // Removed manual history.pushState - let React Router handle all navigation
      }
    } else if (initialSelectedChildId === null) {
      // Clear selected child when parent clears the selection
      setSelectedChild(null);
    }
  }, [initialSelectedChildId, children]);

  const handleChildClick = (child: Child) => {
    // Use proper navigation to change the URL
    if (onNavigateToChildActivities) {
      onNavigateToChildActivities(child);
    } else {
      // Fallback to old behavior if navigation prop not provided
      setSelectedChild(child);
      onChildSelectionChange?.(child.uuid);
    }
  };

  const handleBackToChildren = () => {
    // Check if any invitations were viewed and refresh data if needed
    if (sessionStorage.getItem('invitationViewed') === 'true') {
      sessionStorage.removeItem('invitationViewed');
      // Refresh all data to remove viewed invitations from notifications
      loadChildren();
    }
    
    setSelectedChild(null);
    onChildSelectionChange?.(null);
    
    // Scroll to top before navigating back
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Check if we have a navigation state indicating we came from calendar
    
    // Try multiple approaches to detect if we came from calendar
    const fromState = location.state?.fromPath === '/calendar';
    const fromSession = sessionStorage.getItem('navigationSource') === 'calendar';
    const cameFromCalendar = fromState || fromSession;
    
    
    if (cameFromCalendar) {
      // Clear the session storage flag
      sessionStorage.removeItem('navigationSource');
      // Direct navigation to calendar instead of trying to manipulate history
      navigate('/calendar', { replace: true });
      return;
    }
    
    // Use proper navigation if available, otherwise fallback to browser back
    if (onNavigateBack) {
      onNavigateBack();
    } else {
      window.history.back();
    }
  };

  if (loading) {
    return (
      <div className="children-screen">
        <div className="loading">Loading children...</div>
      </div>
    );
  }

  // Show child activity screen if a child is selected
  if (selectedChild) {
    return <ChildActivityScreen 
      child={selectedChild} 
      onBack={handleBackToChildren}
      onDataChanged={loadChildren}
      onNavigateToConnections={onNavigateToConnections}
      onNavigateToActivity={onNavigateToActivity}
      initialActivityUuid={initialActivityUuid}
      shouldRestoreActivityCreation={shouldRestoreActivityCreation}
    />;
  }

  return (
    <div className="children-screen">
      <div className="children-header">
        <h2>My Children</h2>
        <button onClick={handleAddChild} className="add-btn">
          + Add Child
        </button>
      </div>

      {/* Activity Draft Notification */}
      {activityDraft && (
        <div style={{
          background: 'linear-gradient(135deg, #667eea, #764ba2)',
          color: 'white',
          padding: '16px',
          borderRadius: '8px',
          margin: '16px 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div>
            <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>
              📝 Saved Activity Draft
            </h4>
            <p style={{ margin: '0', fontSize: '14px', opacity: 0.9 }}>
              You have an unfinished activity "{activityDraft.newActivity?.name || 'Untitled'}" 
              {activityDraft.childUuid && children.find(c => c.uuid === activityDraft.childUuid) && 
                ` for ${children.find(c => c.uuid === activityDraft.childUuid)?.name}`}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={resumeActivityCreation}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              Continue
            </button>
            <button
              onClick={dismissActivityDraft}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                color: 'white',
                padding: '8px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {!Array.isArray(children) || children.length === 0 ? (
        <div className="empty-state">
          <h3>No Children Added</h3>
          <p>Add your first child to start sharing activities</p>
          <button onClick={handleAddChild} className="add-first-btn">
            Add Child
          </button>
        </div>
      ) : (
        <div className="children-grid">
          {children.map((child) => (
            <div
              key={child.uuid}
              className="child-card"
              onClick={() => handleChildClick(child)}
            >
              <div className="child-info">
                <h3 className="child-name">{child.display_name || child.name}</h3>
                <div className="child-details">
                  {child.age && `Age: ${child.age}`}
                  {child.age && child.grade && ' • '}
                  {child.grade && `Grade: ${child.grade}`}
                </div>
                {child.school && (
                  <div className="child-school">School: {child.school}</div>
                )}
                {child.interests && (
                  <div className="child-interests">Interests: {child.interests}</div>
                )}

                {/* Activity Count Badge */}
                <div className="child-activity-count">
                  {childActivityCounts[child.uuid] || 0} {childActivityCounts[child.uuid] === 1 ? 'Activity' : 'Activities'}
                </div>

                {/* Detailed Notifications */}
                {((childInvitations[child.uuid] && childInvitations[child.uuid].length > 0) ||
                  (childConnections[child.uuid] && childConnections[child.uuid].length > 0) ||
                  (childSystemNotifications[child.uuid] && childSystemNotifications[child.uuid].length > 0)) && (
                  <div className="child-invitations">
                    <div className="invitations-header">
                      📩 {(childInvitations[child.uuid]?.length || 0) + (childConnections[child.uuid]?.length || 0) + (childSystemNotifications[child.uuid]?.length || 0)} Notification{((childInvitations[child.uuid]?.length || 0) + (childConnections[child.uuid]?.length || 0) + (childSystemNotifications[child.uuid]?.length || 0)) !== 1 ? 's' : ''}
                    </div>

                    {/* Activity Invitations */}
                    {childInvitations[child.uuid] && childInvitations[child.uuid]
                      .filter((invitation, index, arr) => {
                        // Handle recurring series - only show the first invitation of each series
                        if ((invitation as any).isRecurringSeries) {
                          const seriesId = (invitation as any).seriesId;
                          if (seriesId) {
                            const seriesInvitations = arr.filter(inv => (inv as any).seriesId === seriesId);
                            const sortedSeries = seriesInvitations.sort((a, b) =>
                              new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
                            );
                            const shouldShow = invitation.id === sortedSeries[0].id;
                            return shouldShow;
                          }
                        }
                        return true; // Show all individual activities
                      })
                      .map((invitation) => {
                        const childFullName = invitation.host_child_name || 'Someone';

                        const formatDate = (dateString: string) => {
                          const date = new Date(dateString);
                          const day = String(date.getDate()).padStart(2, '0');
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const year = date.getFullYear();
                          return `${day}/${month}/${year}`;
                        };

                        const timeRange = invitation.start_time && invitation.end_time
                          ? ` at ${invitation.start_time}-${invitation.end_time}`
                          : invitation.start_time
                          ? ` at ${invitation.start_time}`
                          : '';

                        let displayMessage = '';
                        if ((invitation as any).invitation_message) {
                          displayMessage = (invitation as any).invitation_message;
                        } else if ((invitation as any).isRecurringSeries) {
                          const activityTypeText = invitation.activity_type ? ` (${invitation.activity_type})` : '';
                          displayMessage = invitation.activity_description || `${childFullName} invited you to "${invitation.activity_name}"${activityTypeText}`;
                        } else {
                          const activityTypeText = invitation.activity_type ? ` (${invitation.activity_type})` : '';
                          displayMessage = `${childFullName} invited you to "${invitation.activity_name}"${activityTypeText} on ${formatDate(invitation.start_date)}${timeRange}`;
                        }

                        // Debug logging for tentative status
                        console.log('🔍 Invitation button render:', {
                          id: invitation.id,
                          invitation_uuid: invitation.invitation_uuid,
                          activity_name: invitation.activity_name,
                          is_tentative: invitation.is_tentative,
                          is_tentative_type: typeof invitation.is_tentative,
                          full_invitation: invitation
                        });

                        const isAccepted = invitation.status === 'accepted';
                        const isPending = invitation.status === 'pending';

                        return (
                          <div key={invitation.id} className="invitation-item" onClick={(e) => e.stopPropagation()}>
                            <div className="invitation-message">
                              {displayMessage}
                            </div>
                            <div className="invitation-actions">
                              {/* Show Accept button only for pending invitations */}
                              {isPending && (
                                <button
                                  className="invitation-accept-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const responseId = invitation.invitation_uuid || String(invitation.id);
                                    handleInvitationResponse(responseId, 'accept');
                                  }}
                                  disabled={processingInvitation === invitation.id}
                                >
                                  {processingInvitation === invitation.id ? '...' : (invitation.is_tentative ? "I'm Interested" : 'Accept')}
                                </button>
                              )}

                              {/* Always show Decline button - for pending to reject, for accepted to change mind */}
                              <button
                                className="invitation-reject-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const responseId = invitation.invitation_uuid || String(invitation.id);
                                  handleInvitationResponse(responseId, 'reject');
                                }}
                                disabled={processingInvitation === invitation.id}
                              >
                                {processingInvitation === invitation.id ? '...' : (invitation.is_tentative ? 'Not Interested' : 'Decline')}
                              </button>

                              <button
                                className="invitation-calendar-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Only navigate with date if start_date is valid
                                  const validStartDate = invitation.start_date && invitation.start_date !== 'undefined' ? invitation.start_date : undefined;
                                  onNavigateToChildCalendar?.(child, validStartDate);
                                }}
                              >
                                Calendar
                              </button>
                            </div>
                          </div>
                        );
                      })}

                    {/* Connection Requests */}
                    {childConnections[child.uuid] && childConnections[child.uuid].map((request) => {
                      return (
                        <div key={`connection_${request.id}`} className="invitation-item" onClick={(e) => e.stopPropagation()}>
                          <div className="invitation-message">
                            {request.child_name || 'Someone'} wants to connect
                          </div>
                          <div className="invitation-actions">
                            <button
                              className="invitation-accept-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleConnectionResponse(request.request_uuid, 'accept');
                              }}
                              disabled={processingConnection === request.request_uuid}
                            >
                              {processingConnection === request.request_uuid ? '...' : 'Accept'}
                            </button>
                            <button
                              className="invitation-reject-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleConnectionResponse(request.request_uuid, 'reject');
                              }}
                              disabled={processingConnection === request.request_uuid}
                            >
                              {processingConnection === request.request_uuid ? '...' : 'Decline'}
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    {/* System Notifications & Activity Reminders */}
                    {childSystemNotifications[child.uuid] && childSystemNotifications[child.uuid].map((notification) => {
                      const formatDate = (dateString: string) => {
                        const date = new Date(dateString);
                        const day = String(date.getDate()).padStart(2, '0');
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const year = date.getFullYear();
                        return `${day}/${month}/${year}`;
                      };

                      const getNotificationIcon = (type: string) => {
                        switch (type) {
                          case 'activity_reminder': return '⏰';
                          case 'connection_rejected': return '❌';
                          case 'connection_accepted': return '✅';
                          case 'activity_rejected': return '❌';
                          case 'activity_accepted': return '✅';
                          case 'guest_decline': return '❌';
                          case 'guest_decline_pending': return '❌';
                          case 'tentative_response': return '👍';
                          case 'system':
                          default: return '📢';
                        }
                      };

                      const icon = getNotificationIcon(notification.type);

                      return (
                        <div key={notification.id} className="invitation-item" onClick={(e) => e.stopPropagation()}>
                          <div className="invitation-message">
                            {icon} {notification.message} ({formatDate(notification.timestamp)})
                          </div>
                          <div className="invitation-actions">
                            <button
                              className="invitation-calendar-btn"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  // Dismiss the system notification
                                  await apiService.dismissNotification(notification.id, notification.type);
                                  // Remove from local state
                                  setChildSystemNotifications(prev => ({
                                    ...prev,
                                    [child.uuid]: prev[child.uuid].filter(n => n.id !== notification.id)
                                  }));
                                } catch (error) {
                                  console.error('Failed to dismiss notification:', error);
                                }
                              }}
                            >
                              {notification.type === 'connection_accepted' || notification.type === 'connection_rejected' ? 'Dismiss' : 'Mark as Read'}
                            </button>
                            {/* Only show View Calendar for activity reminders, not for connection notifications */}
                            {notification.type !== 'connection_accepted' && notification.type !== 'connection_rejected' && (
                              <button
                                className="invitation-calendar-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Navigate to the child's activity screen and jump to activity date
                                  const activityDate = notification.data?.activity_date || notification.data?.activity_start_date || notification.data?.start_date;
                                  if (activityDate && activityDate !== 'undefined') {
                                    // Navigate with the specific date to open calendar at that week
                                    navigate(`/children/${child.uuid}/activities?date=${activityDate}&view=week`);
                                  } else {
                                    // Fallback: just navigate to activities
                                    handleChildClick(child);
                                  }
                                }}
                              >
                                View Calendar
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="child-date">
                  Added: {formatDateUK(child.created_at)}
                </div>
              </div>


              {/* Child Actions */}
              <div className="child-actions">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditChild(child);
                  }}
                  className="edit-btn"
                >
                  Edit
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteChild(child);
                  }}
                  className="delete-btn"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Child Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Add New Child</h3>
            <div className="name-fields">
              <input
                type="text"
                placeholder="First name"
                value={newChildFirstName}
                onChange={(e) => setNewChildFirstName(e.target.value)}
                className="modal-input"
                autoFocus
              />
              <input
                type="text"
                placeholder="Last name"
                value={newChildLastName}
                onChange={(e) => setNewChildLastName(e.target.value)}
                className="modal-input"
              />
            </div>
            <label className="modal-label">Date of Birth (Optional)</label>
            <input
              type="date"
              value={newChildBirthDate}
              onChange={(e) => setNewChildBirthDate(e.target.value)}
              className="modal-input"
              max={new Date().toISOString().split('T')[0]}
            />
            <label className="modal-label">School Name (Optional)</label>
            <input
              type="text"
              placeholder="e.g., St Mary's School"
              value={newChildSchoolVenue}
              onChange={(e) => setNewChildSchoolVenue(e.target.value)}
              className="modal-input"
            />
            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewChildFirstName('');
                  setNewChildLastName('');
                  setNewChildBirthDate('');
                  setNewChildSchoolVenue('');
                  setNewChildSchoolCity('');
                }}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateChild}
                disabled={addingChild}
                className={`confirm-btn ${addingChild ? 'disabled' : ''}`}
              >
                {addingChild ? 'Adding...' : 'Add Child'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Child Modal */}
      {(() => {
        const shouldShow = showEditModal || (editChildUuid && children.length > 0);
        return shouldShow;
      })() && (() => {
        // Handle skeleton child from URL parameter
        const skeletonChild = editChildUuid ? children.find(child => child.uuid === editChildUuid) : null;
        const currentChild = editingChild || skeletonChild;


        // Initialize form data for skeleton child if needed
        if (skeletonChild && !editingChild) {
          // Set up the form data for the skeleton child
          setTimeout(() => {
            setEditingChild(skeletonChild);

            // For skeleton children, first_name and last_name are typically null
            // Try to extract reasonable names from the combined name field
            let extractedFirstName = skeletonChild.first_name || '';
            let extractedLastName = skeletonChild.last_name || '';

            if (!extractedFirstName && !extractedLastName && skeletonChild.name) {
              // Try to extract from combined name like "skel466@example.com skel466@example.com"
              const nameParts = skeletonChild.name.split(' ');
              if (nameParts.length >= 2) {
                // Take first part as first name, last part as last name
                extractedFirstName = nameParts[0];
                extractedLastName = nameParts[nameParts.length - 1];

                // If they're the same (like email repeated), just use the first part
                if (extractedFirstName === extractedLastName) {
                  extractedLastName = '';
                }
              } else if (nameParts.length === 1) {
                extractedFirstName = nameParts[0];
              }
            }

            setEditChildFirstName(extractedFirstName);
            setEditChildLastName(extractedLastName);
            setEditChildBirthDate(''); // Start with empty birth date for skeleton children
            setEditChildInterests(skeletonChild.interests || '');
            setEditChildSchoolVenue(skeletonChild.school_venue || '');
            setEditChildSchoolCity(skeletonChild.school_city || '');
          }, 0);
        }
        return (
          <div className="modal-overlay edit-child-modal-overlay" onClick={handleCancelEdit}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h2>Edit Child Details</h2>
            <div className="form-group">
              <label>First Name *</label>
              <input
                type="text"
                value={editChildFirstName}
                onChange={(e) => setEditChildFirstName(e.target.value)}
                placeholder="Enter first name"
                className="modal-input"
              />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                value={editChildLastName}
                onChange={(e) => setEditChildLastName(e.target.value)}
                placeholder="Enter last name"
                className="modal-input"
              />
            </div>
            <div className="form-group">
              <label>Date of Birth</label>
              <input
                type="date"
                value={editChildBirthDate}
                onChange={(e) => setEditChildBirthDate(e.target.value)}
                className="modal-input"
                max={new Date().toISOString().split('T')[0]} // Can't be future date
              />
            </div>
            <div className="form-group">
              <label>School Name (Optional)</label>
              <input
                type="text"
                value={editChildSchoolVenue}
                onChange={(e) => setEditChildSchoolVenue(e.target.value)}
                placeholder="e.g., St Mary's School"
                className="modal-input"
              />
            </div>
            <div className="modal-actions">
              <button
                onClick={handleCancelEdit}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateChild}
                disabled={updatingChild}
                className={`confirm-btn ${updatingChild ? 'disabled' : ''}`}
              >
                {updatingChild ? 'Updating...' : 'Update Child'}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Designated Parent Selection Modal */}
      {showDesignatedParentModal && (
        <div className="modal-overlay" onClick={() => setShowDesignatedParentModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Select Designated Parent</h3>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
              Who will be responsible for taking the child to this activity?
            </p>

            <select
              className="modal-input"
              value={selectedParentUuid}
              onChange={(e) => setSelectedParentUuid(e.target.value)}
              style={{ marginBottom: '16px' }}
            >
              <option value="">Select a parent...</option>
              {parents.map((parent) => (
                <option key={parent.uuid} value={parent.uuid}>
                  {parent.username}
                </option>
              ))}
            </select>

            <div className="modal-actions" style={{ display: 'flex', gap: '8px' }}>
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowDesignatedParentModal(false);
                  setProcessingInvitation(null);
                }}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                className="skip-btn"
                onClick={() => handleDesignatedParentConfirm(true)}
                style={{ flex: 1, background: '#6c757d', color: 'white' }}
              >
                Skip
              </button>
              <button
                className="confirm-btn"
                onClick={() => handleDesignatedParentConfirm(false)}
                disabled={!selectedParentUuid}
                style={{ flex: 1 }}
              >
                Confirm & Accept
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChildrenScreen;